import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components';
import { useAuth } from '../context/AuthContext';
import { authenticatedHomePath } from '../utils/onboarding';
import { apiErrorCode, apiErrorStatus, apiErrorText } from '../utils/apiError';

type ActivationError = {
  message: string;
  requiresOriginalSession: boolean;
};

function activationErrorDetails(
  error: unknown,
  t: (key: string, options?: Record<string, unknown>) => string
): ActivationError {
  const code = apiErrorCode(error);
  const status = apiErrorStatus(error);

  // A plain 403 is the staff-role check: the link was opened in another login.
  if (
    code === 'IMPERSONATION_SESSION_MISMATCH' ||
    (status === 403 && code === 'FORBIDDEN')
  ) {
    return {
      message: t('impersonationActivate.errorSessionMismatch'),
      requiresOriginalSession: true
    };
  }

  if (status === 401) {
    return {
      message: t('impersonationActivate.errorSessionExpired'),
      requiresOriginalSession: false
    };
  }

  return {
    message: apiErrorText(t, error, 'impersonationActivate.errorInvalidLink'),
    requiresOriginalSession: false
  };
}

export function ImpersonationActivate() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { activateImpersonation, user } = useAuth();
  const started = useRef(false);
  const [error, setError] = useState<ActivationError | null>(null);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const token = searchParams.get('token');
    if (!token) {
      setError({
        message: t('impersonationActivate.errorMissingToken'),
        requiresOriginalSession: false
      });
      return;
    }
    activateImpersonation(token)
      .then((user) => navigate(authenticatedHomePath(user), { replace: true }))
      .catch((activationError: unknown) => {
        setError(activationErrorDetails(activationError, t));
      });
  }, [activateImpersonation, navigate, searchParams, t]);

  return (
    <DashboardLayout>
      <div className="container py-5" style={{ maxWidth: 680 }}>
        <div className="card border-0 shadow-sm">
          <div className="card-body p-4 p-md-5 text-center">
            {error ? (
              <>
                <div className="text-danger fs-1 mb-3" aria-hidden="true">
                  <i className="feather-alert-triangle" />
                </div>
                <h1 className="h4">{t('impersonationActivate.errorTitle')}</h1>
                <p className="text-muted">{error.message}</p>
                {error.requiresOriginalSession && user && (
                  <div className="alert alert-warning text-start" role="alert">
                    <strong>{t('impersonationActivate.loggedInAccount')}</strong>{' '}
                    {user.fullName} ({user.email})
                  </div>
                )}
                <Link className="btn btn-primary" to={authenticatedHomePath(user)}>
                  {t('impersonationActivate.backToHome')}
                </Link>
              </>
            ) : (
              <>
                <div className="spinner-border text-warning mb-3" role="status">
                  <span className="visually-hidden">{t('impersonationActivate.startingSr')}</span>
                </div>
                <h1 className="h4">{t('impersonationActivate.verifyingTitle')}</h1>
                <p className="text-muted mb-0">
                  {t('impersonationActivate.verifyingDesc')}
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
